import React, { useState, useEffect } from 'react';
import { Form, Select, Input, InputNumber, Table, Spin, Button, Row, Col, Modal, App } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../../../utils/api';
import { PageTitle } from '../../common';

const { Option } = Select;

interface PadronData {
  id: number;
  razon: string;
  direccion: string;
  localidad: string;
  provincia: string;
}

interface OrdenData {
  id: number;
  id_insumo: number;
  orden: string;
  insumo: number;
  insumo_descripcion: string;
  cantidad: number;
  fecha_entrega: string;
  tasaiva: number;
  precio_unitario: number;
  subtotal: number;
  iva: number;
  iinterno: number;
}

interface NuevoGridData {
  id: number;
  id_insumo: number;
  detalle_insumo: string;
  fecha_entrega: string;
  fecha_emision: string;
  cantidad_ingresada: number;
  orden: string;
  cantidad_facturada: number;
  precio_unitario: number;
  impuesto_interno: number;
  subtotal_siniva: number;
  tasaiva: number;
  impuesto_iva: number;
  total_linea: number;
  tipo_movimiento: string;
  id_empresa: number;
  id_sucursal: number;
}

interface Percepcion {
  id: number;
  provincia: string;
  sigla: string;
  alta: number;
}

// Agregar nueva interface para el grid de percepciones
interface PercepcionGridData {
  id: number;
  descripcion: string;
  importe: number;
}

// Agregar las interfaces necesarias
interface ComprobanteData {
  tipo: string;
  nro_comprobante: string;
  padron: number;
  fecha_emision: string;
  id_empresa: number;
  id_sucursal: number;
}

interface MovimientoData {
  fecha_emision: string;
  fecha_entrega: string;
  insumo: number;
  cantidad: number;
  cantidad_facturada: number;
  tasaiva: number;
  precio_unitario: number;
  subtotal: number;
  iva: number;
  impuesto_interno: number;
  tipo_movimiento: string;
  comprobante: number;
  ordencompra: string;
  id_empresa: number;
  id_sucursal: number;
}

interface PercepcionData {
  tipo: string;
  percepcion: number;
  comprobante: number;
  importe: number;
  id_empresa: number;
  id_sucursal: number;
}

// Nueva interfaz para agrupar todos los datos
interface ComprobanteCompleto {
  comprobante: {
    tipo: string;
    nro_comprobante: string;
    padron: number;
    fecha_emision: string;
    id_empresa: number;
    id_sucursal: number;
  };
  movimientos: Array<{
    fecha_emision: string;
    fecha_entrega: string;
    insumo: number;
    cantidad: number;
    cantidad_facturada: number;
    tasaiva: number;
    precio_unitario: number;
    subtotal: number;
    iva: number;
    impuesto_interno: number;
    tipo_movimiento: string;
    ordencompra: string;
    id_empresa: number;
    id_sucursal: number;
  }>;
  percepciones: Array<{
    tipo: string;
    percepcion: number;
    importe: number;
    id_empresa: number;
    id_sucursal: number;
  }>;
}

// Actualizar la interface de insumos para incluir tasaiva
interface Insumo {
  id: number;
  descripcion: string;
  tasaiva: number;
}

const CrearIngresos: React.FC = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const { getAccessTokenSilently, user, isLoading: isLoadingAuth } = useAuth0();
  const [padrones, setPadrones] = useState<PadronData[]>([]);
  const [ordenes, setOrdenes] = useState<OrdenData[]>([]);
  const [isLoadingOrdenes, setIsLoadingOrdenes] = useState<boolean>(false);
  const empresa = user?.id_empresa;
  const sucursal=user?.id_sucursal;
  const [puntoVenta, setPuntoVenta] = useState<string>('');
  const [numeroComprobante, setNumeroComprobante] = useState<string>('');
  const [impuestoInterno, setImpuestoInterno] = useState<number>(0);
  const [subtotalSinIva, setSubtotalSinIva] = useState<number>(0);
  const [impuestoIva, setImpuestoIva] = useState<number>(0);
  const [subLinea, setSubLinea] = useState<number>(0);
  const [ordenCompra, setOrdenCompra] = useState<string>('');
  const [descripcionInsumo, setDescripcionInsumo] = useState<string>('');
  const [cantidadIngresada, setCantidadIngresada] = useState<number>(0);
  const [cantidadFacturada, setCantidadFacturada] = useState<number>(0);
  const [precioUnitario, setPrecioUnitario] = useState<number>(0);
  const [isLoadingPadron, setIsLoadingPadron] = useState<boolean>(true);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [selectedTasaIva, setSelectedTasaIva] = useState<string>('0');
  const [datosNuevo, setDatosNuevo] = useState<NuevoGridData[]>([]);
  const [totalImpuestoInterno, setTotalImpuestoInterno] = useState<number>(0);
  const [totalSubtotalSinIva, setTotalSubtotalSinIva] = useState<number>(0);
  const [totalIva, setTotalIva] = useState<number>(0);
  const [totalGeneral, setTotalGeneral] = useState<number>(0);
  const [totalPercepciones, setTotalPercepciones] = useState<number>(0);
  const [percepciones, setPercepciones] = useState<Percepcion[]>([]);
  const [selectedPercepcion, setSelectedPercepcion] = useState<string>('');
  const [loadingPercepciones, setLoadingPercepciones] = useState<boolean>(false);
  const [valorPercepcion, setValorPercepcion] = useState<number>(0);
  const [percepcionesGrid, setPercepcionesGrid] = useState<PercepcionGridData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [insumos, setInsumos] = useState<Insumo[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState<OrdenData | null>(null);
  const [cantidadTemp, setCantidadTemp] = useState<number>(0);
  const [precioUnitarioTemp, setPrecioUnitarioTemp] = useState<number>(0);
  const [impuestoInternoTemp, setImpuestoInternoTemp] = useState<number>(0);

  // Función para cargar los padrones
  const fetchPadrones = async () => {
    if (!empresa) {
      console.warn('No se puede cargar padrones: empresa no disponible');
      setIsLoadingPadron(false);
      return;
    }

    setIsLoadingPadron(true);
    try {
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get<PadronData[]>(
        `${process.env.REACT_APP_INSUMOS_URL}/listar_padron/${empresa}`,
        { token }
      );
      
      setPadrones(response.data);
    } catch (error: any) {
      console.error('Error cargando padrones:', error);
      console.error('URL:', `${process.env.REACT_APP_INSUMOS_URL}/listar_padron/${empresa}`);
      console.error('Empresa:', empresa);
      console.error('Response data:', error.response?.data);
      
      let errorMsg = 'Error al cargar los padrones';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      message.error(errorMsg);
    } finally {
      setIsLoadingPadron(false);
    }
  };

  // Función para cargar las órdenes del proveedor
  const fetchOrdenes = async (proveedorId: number) => {
    setIsLoadingOrdenes(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/pendiente_ocompra_dos/${empresa}/${sucursal}/${proveedorId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error('Error al cargar órdenes');
      }

      const data = await response.json();
      console.log('Datos recibidos:', JSON.stringify(data, null, 2));
      setOrdenes(data);
      console.log('xaqui_orden_compra',data,ordenes);
    } catch (error) {
      console.error('Error cargando órdenes:', error);
      message.error('Error al cargar las órdenes del proveedor');
    } finally {
      setIsLoadingOrdenes(false);
    }
  };

  // Manejador del cambio de proveedor
  const handleProveedorChange = (value: number) => {
    const padron = padrones.find(p => p.id === value);
    if (padron) {
      fetchOrdenes(padron.id);
    }
  };

  // Cargar padrones cuando el componente se monta
  useEffect(() => {
    if (!isLoadingAuth && empresa) {
      fetchPadrones();
    }
  }, [isLoadingAuth, empresa]);

  const onFinish = (values: any) => {
    console.log('Valores del formulario:', values);
  };

  // Manejador para el botón Ingresar
  const handleIngresar = (record: OrdenData) => {
    setOrdenSeleccionada(record);
    setCantidadTemp(record.cantidad);
    setPrecioUnitarioTemp(record.precio_unitario);
    setImpuestoInternoTemp(record.iinterno || 0);
    setIsModalVisible(true);
  };

  // Agregar función para manejar la confirmación del modal
  const handleModalConfirm = () => {
    if (!ordenSeleccionada) return;

    // Determinar las cantidades según el tipo de comprobante
    const tipoComprobante = form.getFieldValue('tipoComprobante');
    let cantidadEntregada = 0;
    let cantidadAFacturar = 0;

    switch (tipoComprobante) {
      case 'FAC':
        cantidadEntregada = 0;
        cantidadAFacturar = cantidadTemp;
        break;
      case 'RMT':
        cantidadEntregada = cantidadTemp;
        cantidadAFacturar = 0;
        break;
      case 'F/R':
        cantidadEntregada = cantidadTemp;
        cantidadAFacturar = cantidadTemp;
        break;
      default:
        cantidadEntregada = cantidadTemp;
        cantidadAFacturar = cantidadTemp;
    }

    // 1. Calcular el total (precio unitario x cantidad)
    const total = cantidadAFacturar * precioUnitarioTemp;

    // 2. Calcular subtotal sin IVA = (Total - impuesto interno) * 100 / (100 + tasaiva)
    const subtotalSinIva = ((total - impuestoInternoTemp) * 100) / (100 + ordenSeleccionada.tasaiva);

    // 3. Calcular IVA = total - impuesto interno - subtotal sin IVA
    const iva = total - impuestoInternoTemp - subtotalSinIva;

    console.log('Cálculos:', {
      total,
      subtotalSinIva,
      iva,
      impuestoInterno: impuestoInternoTemp
    });

    const nuevosDatos: NuevoGridData = {
      id: ordenSeleccionada.id,
      id_insumo: ordenSeleccionada.id_insumo,
      detalle_insumo: ordenSeleccionada.insumo_descripcion,
      fecha_entrega: ordenSeleccionada.fecha_entrega,
      fecha_emision: ordenSeleccionada.fecha_entrega,
      cantidad_ingresada: cantidadEntregada,
      orden: ordenSeleccionada.orden,
      cantidad_facturada: cantidadAFacturar,
      precio_unitario: precioUnitarioTemp,
      impuesto_interno: impuestoInternoTemp,
      subtotal_siniva: Number(subtotalSinIva.toFixed(2)),
      tasaiva: ordenSeleccionada.tasaiva,
      impuesto_iva: Number(iva.toFixed(2)),
      total_linea: Number(total.toFixed(2)),
      tipo_movimiento: '002',
      id_empresa: empresa || 0,
      id_sucursal: sucursal || 0
    };

    setDatosNuevo(prevDatos => [...(Array.isArray(prevDatos) ? prevDatos : []), nuevosDatos]);
    setIsModalVisible(false);
    message.success('Ítem agregado correctamente');
  };

  // Actualizar la definición de las columnas
  const columns: ColumnsType<OrdenData> = [
    {
      title: 'Id',
      dataIndex: 'id',
      width: '5%',
      align: 'right'
    },
    {
      title: 'Orden',
      dataIndex: 'orden',
      width: '15%',
    },
    {
      title: 'Insumo',
      dataIndex: 'insumo_descripcion',
      width: '25%',
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Tasa<br />Iva %
        </div>
      ),
      dataIndex: 'tasaiva',
      width: '8%',
      align: 'right',
      render: (value: number) => {
        const numValue = parseFloat(value.toString());
        return numValue ? numValue.toFixed(2) : '0.00';
      }
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Cantidad<br />Entregada
        </div>
      ),
      dataIndex: 'cantidad',
      width: '10%',
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00'
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Cantidad<br />Facturada
        </div>
      ),
      dataIndex: 'cantidad_facturada',
      width: '10%',
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00'
    },
    {
      title: 'Unitario',
      dataIndex: 'precio_unitario',
      width: '12%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      width: '12%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Acción',
      key: 'action',
      width: '8%',
      align: 'center',
      render: (_, record) => {
        const estaEnNuevo = datosNuevo.some(item => item.id === record.id);
        return (
          <Button
            type="primary"
            size="small"
            danger={estaEnNuevo}
            disabled={estaEnNuevo}
            onClick={() => handleIngresar(record)}
          >
            Ingresar
          </Button>
        );
      },
    }
  ];

  // Función para rellenar con ceros
  const padWithZeros = (num: string, targetLength: number): string => {
    return num.padStart(targetLength, '0');
  };

  // Función para formatear números con ceros a la izquierda
  const padLeftZeros = (num: string, length: number) => {
    return num.toString().padStart(length, '0');
  };

  // Manejadores para los inputs
  const handlePuntoVentaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remover cualquier caracter que no sea número
    const value = e.target.value.replace(/\D/g, '');
    // Limitar a 4 dígitos
    setPuntoVenta(value.slice(0, 4));
  };

  const handlePuntoVentaBlur = () => {
    // Rellenar con ceros cuando pierde el foco
    setPuntoVenta(padWithZeros(puntoVenta, 4));
  };

  const handleNumeroComprobanteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remover cualquier caracter que no sea número
    const value = e.target.value.replace(/\D/g, '');
    // Limitar a 8 dígitos
    setNumeroComprobante(value.slice(0, 8));
  };

  const handleNumeroComprobanteBlur = () => {
    // Rellenar con ceros cuando pierde el foco
    setNumeroComprobante(padWithZeros(numeroComprobante, 8));
  };

  // Función para calcular SubLinea
  useEffect(() => {
    const total = impuestoInterno + subtotalSinIva + impuestoIva;
    setSubLinea(total);
  }, [impuestoInterno, subtotalSinIva, impuestoIva]);

  // Actualizar la función recalcularValores
  const recalcularValores = (cantidad: number, precio: number, tasaIva: number, iinterno: number) => {
    // Calcular subtotal bruto (precio * cantidad)
    const subtotalBruto = cantidad * precio;
    
    // Calcular subtotal sin IVA = (precio * cantidad - iinterno) / (1 + tasaIva/100)
    const subtotalSinIva = Number(((subtotalBruto - iinterno) / (1 + tasaIva/100)).toFixed(2));
    setSubtotalSinIva(subtotalSinIva);
    
    // Calcular IVA = (subtotalBruto - iinterno) - subtotalSinIva
    const iva = Number(((subtotalBruto - iinterno) - subtotalSinIva).toFixed(2));
    setImpuestoIva(iva);
    console.log('1',subtotalBruto,subtotalSinIva,iinterno,iva);
  };

  // Actualizar la definición de las columnas del nuevo grid
  const columnasNuevo: ColumnsType<NuevoGridData> = [
    {
      title: 'Id',
      dataIndex: 'id',
      width: '5%',
      align: 'right'
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Detalle<br />Insumos
        </div>
      ),
      dataIndex: 'detalle_insumo',
      width: '20%'
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Cantidad<br />Entregada
        </div>
      ),
      dataIndex: 'cantidad_ingresada',
      width: '10%',
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00'
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Cantidad<br />Facturada
        </div>
      ),
      dataIndex: 'cantidad_facturada',
      width: '10%',
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00'
    },
    {
      title: 'Unitario',
      dataIndex: 'precio_unitario',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'I.Interno',
      dataIndex: 'impuesto_interno',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Sub s/IVA',
      dataIndex: 'subtotal_siniva',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'IVA',
      dataIndex: 'impuesto_iva',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Total',
      dataIndex: 'total_linea',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Acción',
      key: 'action',
      width: '8%',
      align: 'center',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          danger
          onClick={() => handleEliminar(record.id)}
        >
          Eliminar
        </Button>
      ),
    }
  ];

  // Manejador para el botón Aceptar
  const handleAceptar = () => {
    // Validaciones de campos requeridos
    const tipoComprobante = form.getFieldValue('tipoComprobante');
    const letraComprobante = form.getFieldValue('letraComprobante');

    if (!tipoComprobante) {
      message.warning('Debe seleccionar un tipo de comprobante');
      return;
    }

    // Crear el nuevo registro para el grid
    const nuevoItem: NuevoGridData = {
      id: selectedRow || 0,
      id_insumo: insumos.find(i => i.descripcion === descripcionInsumo)?.id || 0,
      detalle_insumo: descripcionInsumo,
      fecha_entrega: new Date().toISOString().split('T')[0],
      fecha_emision: new Date().toISOString().split('T')[0],
      cantidad_ingresada: tipoComprobante === 'FAC' ? 0 : (tipoComprobante === 'NCD' ? -cantidadIngresada : cantidadIngresada),
      orden: ordenCompra,
      cantidad_facturada: tipoComprobante === 'RMT' ? 0 : (tipoComprobante === 'NCD' ? -cantidadFacturada : cantidadFacturada),
      precio_unitario: tipoComprobante === 'RMT' ? 0 : precioUnitario,
      impuesto_interno: tipoComprobante === 'RMT' ? 0 : impuestoInterno,
      subtotal_siniva: tipoComprobante === 'RMT' ? 0 : subtotalSinIva,
      tasaiva: tipoComprobante === 'RMT' ? 0 : Number(selectedTasaIva),
      impuesto_iva: tipoComprobante === 'RMT' ? 0 : impuestoIva,
      total_linea: tipoComprobante === 'RMT' ? 0 : (subtotalSinIva + impuestoIva + impuestoInterno),
      tipo_movimiento: '002',
      id_empresa: empresa || 0,
      id_sucursal: sucursal || 0
    };

    // Verificar si el ítem ya existe en el grid
    const itemExistente = datosNuevo.find(item => item.id === selectedRow);
    if (itemExistente) {
      message.warning('Este ítem ya fue ingresado');
      return;
    }

    // Agregar el nuevo item al grid
    setDatosNuevo([...datosNuevo, nuevoItem]);

    // Actualizar totales solo si no es RMT
    if (tipoComprobante !== 'RMT') {
      setTotalImpuestoInterno(prev => Number((prev + impuestoInterno).toFixed(2)));
      setTotalSubtotalSinIva(prev => Number((prev + subtotalSinIva).toFixed(2)));
      setTotalIva(prev => Number((prev + impuestoIva).toFixed(2)));
      setTotalGeneral(prev => Number((prev + (subtotalSinIva + impuestoIva + impuestoInterno)).toFixed(2)));
    }

    // Limpiar los campos de Items
    setSelectedRow(null);
    setOrdenCompra('');
    setDescripcionInsumo('');
    setCantidadIngresada(0);
    setCantidadFacturada(0);
    setPrecioUnitario(0);
    setImpuestoInterno(0);
    setSubtotalSinIva(0);
    setSelectedTasaIva('0');
    setImpuestoIva(0);

    message.success('Ítem agregado correctamente');
  };

  // Agregar manejador para eliminar registro del grid nuevo
  const handleEliminar = (idRegistro: number) => {
    // Encontrar el item a eliminar
    const itemAEliminar = datosNuevo.find(item => item.id === idRegistro);
    
    if (itemAEliminar) {
      // Actualizar totales restando los valores del item eliminado
      setTotalImpuestoInterno(prev => Number((prev - itemAEliminar.impuesto_interno).toFixed(2)));
      setTotalSubtotalSinIva(prev => Number((prev - itemAEliminar.subtotal_siniva).toFixed(2)));
      setTotalIva(prev => Number((prev - itemAEliminar.impuesto_iva).toFixed(2)));
      setTotalGeneral(prev => Number((prev - itemAEliminar.total_linea).toFixed(2)));
    }

    // Filtrar el registro a eliminar
    const nuevosRegistros = datosNuevo.filter(item => item.id !== idRegistro);
    setDatosNuevo(nuevosRegistros);

    message.success('Ítem eliminado correctamente');
  };

  // Agregamos un useEffect para calcular los totales cuando cambie datosNuevo
  useEffect(() => {
    const totales = datosNuevo.reduce((acc, item) => {
      return {
        impuestoInterno: acc.impuestoInterno + item.impuesto_interno,
        subtotalSinIva: acc.subtotalSinIva + item.subtotal_siniva,
        iva: acc.iva + item.impuesto_iva,
        total: acc.total + item.total_linea
      };
    }, { impuestoInterno: 0, subtotalSinIva: 0, iva: 0, total: 0 });

    setTotalImpuestoInterno(Number(totales.impuestoInterno.toFixed(2)));
    setTotalSubtotalSinIva(Number(totales.subtotalSinIva.toFixed(2)));
    setTotalIva(Number(totales.iva.toFixed(2)));
    setTotalGeneral(Number(totales.total.toFixed(2)));
  }, [datosNuevo]);

  // Función para cargar las percepciones
  const cargarPercepciones = async () => {
    try {
      setLoadingPercepciones(true);
      const token = await getAccessTokenSilently();  // Obtener el token de Auth0    
 //     method: 'GET',
      const ruta = `${process.env.REACT_APP_INSUMOS_URL}/listar_percepciones`;
      const response = await fetch(ruta, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const percepcionesActivas = data.filter((perc: Percepcion) => perc.alta === 1);
      setPercepciones(percepcionesActivas);
      console.log('percepcionesActivas', percepcionesActivas);
    } catch (error) {
      console.error('Error al cargar percepciones:', error);
      message.error('Error al cargar las percepciones');
    } finally {
      setLoadingPercepciones(false);
    }
  };

  // Agregar useEffect para cargar las percepciones al montar el componente
  useEffect(() => {
    cargarPercepciones();
  }, []);

  // Agregar función para manejar el agregar percepción al grid
  const handleAgregarPercepcion = () => {
    if (!selectedPercepcion) {
      message.warning('Debe seleccionar una percepción');
      return;
    }
    if (!valorPercepcion) {
      message.warning('Debe ingresar un valor para la percepción');
      return;
    }

    const percepcionSeleccionada = percepciones.find(p => p.id === Number(selectedPercepcion));
    if (!percepcionSeleccionada) return;

    const nuevaPercepcion: PercepcionGridData = {
      id: percepcionSeleccionada.id,
      descripcion: `${percepcionSeleccionada.provincia} ${percepcionSeleccionada.sigla}`,
      importe: valorPercepcion
    };

    setPercepcionesGrid([...percepcionesGrid, nuevaPercepcion]);
    
    // Limpiar los campos después de agregar
    setSelectedPercepcion('');
    setValorPercepcion(0);
  };

  // Agregar función para eliminar percepción del grid
  const handleEliminarPercepcion = (id: number) => {
    setPercepcionesGrid(percepcionesGrid.filter(item => item.id !== id));
  };

  // Definir las columnas para el grid de percepciones
  const columnasPercepciones: ColumnsType<PercepcionGridData> = [
    {
      title: 'Id',
      dataIndex: 'id',
      width: '10%',
      align: 'right'
    },
    {
      title: 'Percepción',
      dataIndex: 'descripcion',
      width: '30%'
    },
    {
      title: 'Importe',
      dataIndex: 'importe',
      width: '15%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Acción',
      key: 'action',
      width: '15%',
      align: 'center',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          danger
          onClick={() => handleEliminarPercepcion(record.id)}
        >
          Eliminar
        </Button>
      )
    }
  ];

  // Agregar un useEffect para calcular el total de percepciones
  useEffect(() => {
    const total = percepcionesGrid.reduce((sum, item) => sum + item.importe, 0);
    setTotalPercepciones(Number(total.toFixed(2)));
  }, [percepcionesGrid]);

  // Función para reiniciar el formulario
  const handleListado = () => {
    navigate(`/listar_ingresos`);
  };
  
  const handleCancelar = () => {
    // Reiniciar estados del formulario
    form.resetFields();
    setSelectedRow(null);
    setOrdenCompra('');
    setDescripcionInsumo('');
    setCantidadIngresada(0);
    setCantidadFacturada(0);
    setPrecioUnitario(0);
    setImpuestoInterno(0);
    setSubtotalSinIva(0);
    setSelectedTasaIva('0');
    setImpuestoIva(0);
    setSubLinea(0);
    
    // Reiniciar estados de comprobante
    setPuntoVenta('');
    setNumeroComprobante('');
    
    // Reiniciar datos de grids
    setDatosNuevo([]);
    setPercepcionesGrid([]);
    
    // Reiniciar totales
    setTotalImpuestoInterno(0);
    setTotalSubtotalSinIva(0);
    setTotalIva(0);
    setTotalGeneral(0);
    setTotalPercepciones(0);
    
    // Reiniciar percepciones
    setSelectedPercepcion('');
    setValorPercepcion(0);
    
    // Limpiar órdenes si es necesario
    setOrdenes([]);

    message.success('Formulario reiniciado');
  };

  // Función para manejar Finalizar Tarea
  const handleFinalizarTarea = async () => {
    try {
      setIsSaving(true); // Iniciar loading
      const values = await form.validateFields();
      
      // Validaciones
      if (!values.proveedor) {
        message.error('Debe seleccionar un proveedor');
        return;
      }
      if (datosNuevo.length === 0) {
        message.error('Debe ingresar al menos un ítem');
        return;
      }

      // Crear objeto completo con todos los datos
      const comprobanteCompleto: ComprobanteCompleto = {
        comprobante: {
          tipo: values.tipoComprobante,
          nro_comprobante: `${values.letraComprobante}${puntoVenta}${numeroComprobante}`,
          padron: values.proveedor,
          fecha_emision: new Date().toISOString().split('T')[0],
          id_empresa: user?.id_empresa || 0,
          id_sucursal: user?.id_sucursal || 0
        },
        movimientos: datosNuevo.map(item => ({
          fecha_emision: new Date().toISOString().split('T')[0],
          fecha_entrega: item.fecha_entrega,
          insumo: item.id_insumo,
          cantidad: item.cantidad_ingresada,
          cantidad_facturada: item.cantidad_facturada,
          tasaiva: item.tasaiva,
          precio_unitario: item.precio_unitario,
          subtotal: item.subtotal_siniva,
          iva: item.impuesto_iva,
          impuesto_interno: item.impuesto_interno,
          tipo_movimiento: '002',
          ordencompra: item.orden,
          id_empresa: user?.id_empresa || 0,
          id_sucursal: user?.id_sucursal || 0
        })),
        percepciones: percepcionesGrid.map(perc => ({
          tipo: 'PER',
          percepcion: perc.id,
          importe: perc.importe,
          id_empresa: user?.id_empresa || 0,
          id_sucursal: user?.id_sucursal || 0
        }))
      };

      // Verificar datos completos
      if (!comprobanteCompleto.comprobante.id_empresa || !comprobanteCompleto.comprobante.id_sucursal) {
        message.error('Error: Datos de empresa/sucursal no disponibles');
        return;
      }

      console.log('Datos a grabar:', comprobanteCompleto);

      try {
        // Obtener el token de Auth0
        const token = await getAccessTokenSilently();

        // Mostrar mensaje de espera
        message.loading({ 
          content: 'Grabando comprobante...', 
          key: 'saveMessage',
          duration: 0 
        });

        // Enviar todos los datos en una sola llamada
        const ruta = `${process.env.REACT_APP_INSUMOS_URL}/crear_comprobante_completo`;
        const response = await fetch(ruta, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(comprobanteCompleto)
        });

        if (!response.ok) {
          throw new Error('Error al grabar el comprobante');
        }

        const resultado = await response.json();
        console.log('Resultado:', resultado);

        // Actualizar mensaje a éxito
        message.success({ 
          content: 'Comprobante grabado exitosamente', 
          key: 'saveMessage', 
          duration: 2 
        });
        
        handleCancelar();

      } catch (error) {
        console.error('Error al obtener token o grabar:', error);
        // Actualizar mensaje a error
        message.error({ 
          content: 'Error al grabar el comprobante', 
          key: 'saveMessage', 
          duration: 2 
        });
      }

    } catch (error) {
      console.error('Error en validación de formulario:', error);
      message.error('Por favor complete todos los campos requeridos');
    } finally {
      setIsSaving(false); // Finalizar loading
    }
  };

  // Agregar useEffect para el reloj
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString();
      setCurrentTime(timeString);
    };

    // Actualizar inmediatamente
    updateClock();
    
    // Actualizar cada segundo
    const timer = setInterval(updateClock, 1000);

    // Cleanup
    return () => clearInterval(timer);
  }, []);

  // En el useEffect o donde se cargan los datos
  useEffect(() => {
    console.log('Ordenes:', ordenes);
    console.log('DatosNuevo:', datosNuevo);
    console.log('PercepcionesGrid:', percepcionesGrid);
  }, [ordenes, datosNuevo, percepcionesGrid]);

  // Agregar manejador para el cambio de insumo
  const handleInsumoChange = (value: string) => {
    setDescripcionInsumo(value);
    const insumoSeleccionado = insumos.find(i => i.descripcion === value);
    if (insumoSeleccionado) {
      setSelectedTasaIva(insumoSeleccionado.tasaiva.toString());
      // Recalcular valores con la nueva tasa de IVA
      recalcularValores(cantidadFacturada, precioUnitario, insumoSeleccionado.tasaiva, impuestoInterno);
    }
  };

  // Actualizar fetchInsumos para incluir tasaiva
  const fetchInsumos = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_insumos/${user?.id_empresa}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar insumos');
      }

      const data = await response.json();
      setInsumos(data);
    } catch (error) {
      console.error('Error cargando insumos:', error);
      message.error('Error al cargar la lista de insumos');
    }
  };

  // Cargar insumos cuando el componente se monta
  useEffect(() => {
    if (!isLoadingAuth && user?.id_empresa) {
      fetchInsumos();
    }
  }, [isLoadingAuth, user?.id_empresa]);

  // Actualizar los InputNumber en el formulario
  const handleCantidadChange = (value: number | null) => {
    const cantidad = Number(value) || 0;
    const tipoComprobante = form.getFieldValue('tipoComprobante');

    switch (tipoComprobante) {
      case 'FAC':
        setCantidadIngresada(0);
        setCantidadFacturada(cantidad);
        break;
      case 'RMT':
        setCantidadIngresada(cantidad);
        setCantidadFacturada(0);
        break;
      case 'F/R':
        setCantidadIngresada(cantidad);
        setCantidadFacturada(cantidad);
        break;
      default:
        setCantidadIngresada(cantidad);
        setCantidadFacturada(cantidad);
    }
  };

  return (
    <div style={{ padding: '0' }}>
      <style>{`
        /* Asegurar altura consistente para todos los inputs y selects */
        .crear-ingresos-form .ant-select-selector,
        .crear-ingresos-form .ant-input,
        .crear-ingresos-form .ant-input-number-input {
          height: 28px !important;
          line-height: 28px !important;
        }
        .crear-ingresos-form .ant-select-selection-item,
        .crear-ingresos-form .ant-select-selection-placeholder {
          line-height: 26px !important;
        }
      `}</style>
      {/* Título principal y reloj */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '12px',
        position: 'relative'  // Para posicionamiento absoluto del reloj
      }}>
        {/* Contenedor del título centrado */}
        <div style={{ 
          width: '100%',
          textAlign: 'center'  // Centra el texto
        }}>
          <PageTitle level={3} style={{ fontSize: '18px', marginBottom: '0' }}>CREAR INGRESOS</PageTitle>
        </div>
        
        {/* Reloj posicionado a la derecha */}
        <div style={{
          position: 'absolute',  // Posicionamiento absoluto
          right: 0,             // Alineado a la derecha
          backgroundColor: '#f0f2f5',
          padding: '4px 10px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '13px'
        }}>
          {currentTime}
        </div>
      </div>

      {/* Formulario de cabecera */}
      <Form form={form} layout="vertical" className="crear-ingresos-form">
        {/* Contenedor de los controles en línea */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          gap: '8px',
          marginBottom: '12px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Select de proveedor - Alineado a la izquierda */}
          <Form.Item
            name="proveedor"
            rules={[{ required: true, message: 'Por favor seleccione un proveedor' }]}
            style={{ marginBottom: 0 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '28px' }}>
              <span style={{ fontSize: '11px', fontWeight: 500, lineHeight: '28px' }}>Proveedor:</span>
              <Select
                showSearch
                placeholder="Seleccione un proveedor"
                size="small"
                style={{ width: '280px', fontSize: '11px', height: '28px' }}
                optionFilterProp="children"
                onChange={(value) => {
                  handleProveedorChange(value);
                  form.setFieldsValue({ proveedor: value });
                }}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {padrones.map(padron => (
                  <Option key={padron.id} value={padron.id}>
                    {padron.razon}
                  </Option>
                ))}
              </Select>
            </div>
          </Form.Item>

          {/* Contenedor de campos alineados a la derecha */}
          <div style={{ 
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Select de tipo comprobante */}
            <Form.Item
              name="tipoComprobante"
              rules={[{ required: true, message: 'Por favor seleccione el tipo de comprobante' }]}
              style={{ marginBottom: 0 }}
            >
              <Select 
                size="small"
                placeholder="Tipo"
                style={{ width: '120px', fontSize: '11px', height: '28px' }}
                onChange={(value) => {
                  if (value === 'FAC') {
                    setCantidadIngresada(0);
                  }
                  if (value === 'RMT') {
                    setCantidadFacturada(0);
                    setPrecioUnitario(0);
                    setImpuestoInterno(0);
                    setSubtotalSinIva(0);
                    setImpuestoIva(0);
                  }
                }}
              >
                <Option value="FAC">FAC - Factura</Option>
                <Option value="F/R">F/R - Factura/Remito</Option>
                <Option value="RMT">RMT - Remito</Option>
                <Option value="NCD">NCD - Nota de Crédito</Option>
              </Select>
            </Form.Item>

            {/* Letra Comprobante */}
            <Form.Item
              name="letraComprobante"
              rules={[{ required: true, message: 'Por favor seleccione la letra' }]}
              style={{ marginBottom: 0 }}
            >
              <Select size="small" placeholder="Letra" style={{ width: '70px', fontSize: '11px', height: '28px' }}>
                <Option value="A">A</Option>
                <Option value="B">B</Option>
                <Option value="C">C</Option>
                <Option value="X">X</Option>
              </Select>
            </Form.Item>

            {/* Punto de Venta */}
            <Form.Item
              name="puntoVenta"
              rules={[{ required: true, message: 'Por favor ingrese el punto de venta' }]}
              style={{ marginBottom: 0 }}
            >
              <Input
                size="small"
                value={puntoVenta}
                onChange={handlePuntoVentaChange}
                onBlur={handlePuntoVentaBlur}
                maxLength={4}
                style={{ width: '60px', fontSize: '11px', height: '28px' }}
              />
            </Form.Item>

            {/* Número de Comprobante */}
            <Form.Item
              name="numeroComprobante"
              rules={[{ required: true, message: 'Por favor ingrese el número de comprobante' }]}
              style={{ marginBottom: 0 }}
            >
              <Input
                size="small"
                value={numeroComprobante}
                onChange={handleNumeroComprobanteChange}
                onBlur={handleNumeroComprobanteBlur}
                maxLength={8}
                style={{ width: '90px', fontSize: '11px', height: '28px' }}
              />
            </Form.Item>
          </div>
        </div>
      </Form>

      {/* Grid ORDEN */}
      <div style={{ 
        marginBottom: '6px',
        textAlign: 'center'  // Centrar el título
      }}>
        <h3 style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>Órdenes Pendientes</h3>
      </div>
      <div style={{ 
        marginTop: '8px',
        backgroundColor: '#f5f5f5',
        padding: '6px',
        borderRadius: '4px'
      }}>
        <Spin spinning={isLoadingPadron || isLoadingOrdenes} tip="Cargando datos...">
          <Table
            columns={columns}
            dataSource={Array.isArray(ordenes) ? ordenes : []}
            pagination={false}
            scroll={{ y: 120 }}
            size="small"
            style={{ backgroundColor: '#f5f5f5', fontSize: '11px' }}
            rowKey={record => `orden_${record.id}`}
          />
        </Spin>
      </div>

      {/* Detalle del Item */}
      <div style={{ 
        padding: '8px 12px',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        backgroundColor: '#f5f5f5',
        marginBottom: '8px'
      }}>
        {/* Todos los campos en una línea */}
        <div style={{ 
          display: 'flex',
          gap: '8px',
          alignItems: 'flex-end',  // Alinea los inputs en la parte inferior
          flexWrap: 'wrap'
        }}>
          {/* Orden Compra */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: 0 }}>
              Orden Compra
            </label>
            <Input 
              size="small"
              style={{ width: '80px', fontSize: '11px' }}
              maxLength={8}
              readOnly 
              value={ordenCompra}
            />
          </div>

          {/* Descripción Insumo */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: 0 }}>
              Descripción Insumo
            </label>
            <Select
              size="small"
              showSearch
              value={descripcionInsumo}
              onChange={handleInsumoChange}
              style={{ width: '180px', fontSize: '11px' }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {insumos.map(insumo => (
                <Option key={insumo.id} value={insumo.descripcion}>
                  {insumo.descripcion}
                </Option>
              ))}
            </Select>
          </div>

          {/* Cantidad Ingresada/Entregada */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: 0 }}>Cantidad Entregada</label>
            <InputNumber 
              size="small"
              style={{ width: '85px', fontSize: '11px' }}
              precision={2}
              value={cantidadIngresada}
              disabled={form.getFieldValue('tipoComprobante') === 'FAC'}
              onChange={handleCantidadChange}
            />
          </div>

          {/* Cantidad Facturada */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: 0 }}>Cantidad Facturada</label>
            <InputNumber 
              size="small"
              style={{ width: '85px', fontSize: '11px' }}
              precision={2}
              value={cantidadFacturada}
              disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
              onChange={handleCantidadChange}
            />
          </div>

          {/* Precio Unitario */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: 0 }}>Precio Unitario</label>
            <InputNumber 
              size="small"
              style={{ width: '85px', fontSize: '11px' }}
              precision={2}
              prefix="$"
              value={precioUnitario}
              disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
              onChange={value => {
                const precio = Number(value) || 0;
                setPrecioUnitario(precio);
              }}
              onBlur={() => {
                recalcularValores(cantidadFacturada, precioUnitario, Number(selectedTasaIva), impuestoInterno);
              }}
            />
          </div>

          {/* Impuesto Interno */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: 0 }}>Impuesto Interno</label>
            <InputNumber 
              size="small"
              style={{ width: '85px', fontSize: '11px' }}
              precision={2}
              prefix="$"
              value={impuestoInterno}
              disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
              onChange={value => {
                const nuevoValor = Number(value) || 0;
                setImpuestoInterno(nuevoValor);
              }}
              onBlur={() => {
                recalcularValores(cantidadFacturada, precioUnitario, Number(selectedTasaIva), impuestoInterno);
              }}
            />
          </div>

          {/* Subtotal sin IVA */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: 0 }}>Subtotal sin IVA</label>
            <InputNumber 
              size="small"
              style={{ width: '85px', fontSize: '11px' }}
              precision={2}
              prefix="$"
              value={subtotalSinIva}
              disabled={true} // Siempre deshabilitado ya que es calculado
            />
          </div>

          {/* Impuesto IVA */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: 0 }}>Impuesto IVA</label>
            <InputNumber 
              size="small"
              style={{ width: '85px', fontSize: '11px' }}
              precision={2}
              prefix="$"
              value={impuestoIva}
              disabled={true} // Siempre deshabilitado ya que es calculado
            />
          </div>
        </div>
      </div>

      {/* Sección SubLinea y botón Aceptar */}
      <div style={{ 
        marginTop: '8px',
        padding: '8px 12px',
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 'fit-content',
        margin: '8px auto',
      }}>
        <div style={{ 
          display: 'flex', 
          gap: '12px',
          alignItems: 'flex-end',
          width: '100%',
          justifyContent: 'center'
        }}>
          {/* SubLinea */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ 
              fontSize: '11px',
              fontWeight: 500,
              textAlign: 'center',
              marginBottom: 0
            }}>
              SubLinea
            </label>
            <div style={{ 
              fontWeight: 'bold',
              textDecoration: 'underline',
              textAlign: 'right',
              color: 'red'
            }}>
              <InputNumber 
                size="small"
                style={{ width: '100px', fontSize: '11px' }}
                disabled
                precision={2}
                prefix="$"
                value={subLinea}
              />
            </div>
          </div>

          {/* Botón Aceptar */}
          <Button 
            type="primary"
            size="small"
            style={{ height: '28px', fontSize: '12px', marginBottom: '1px' }}
            onClick={handleAceptar}
            disabled={isLoadingPadron}
          >
            Aceptar
          </Button>
        </div>
      </div>

      {/* Grid NUEVO */}
      <div style={{ 
        marginBottom: '6px', 
        marginTop: '12px',
        textAlign: 'center'  // Centrar el título
      }}>
        <h3 style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>Items Ingresados</h3>
      </div>
      <div style={{ 
        backgroundColor: '#f5f5f5',
        padding: '6px',
        borderRadius: '4px'
      }}>
        <Table
          columns={columnasNuevo}
          dataSource={Array.isArray(datosNuevo) ? datosNuevo : []}
          pagination={false}
          scroll={{ y: 120 }}
          size="small"
          style={{ backgroundColor: '#f5f5f5', fontSize: '11px' }}
          rowKey={record => `nuevo_${record.id}`}
          locale={{ 
            emptyText: 'No hay datos'
          }}
        />
      </div>

      {/* Sección de Totales */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: '#f5f5f5',
        padding: '8px 12px',
        borderRadius: '4px',
        marginTop: '8px',
        flexWrap: 'wrap'
      }}>
        {/* Label Totales */}
        <div style={{ 
          fontWeight: 'bold',
          color: '#000000',
          minWidth: '60px',
          fontSize: '12px'
        }}>
          Totales:
        </div>

        {/* Contenedor de los valores */}
        <div style={{ 
          display: 'flex',
          gap: '12px',
          flex: 1,
          flexWrap: 'wrap'
        }}>
          {/* Impuesto Interno */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            justifyContent: 'flex-end'
          }}>
            <label style={{ 
              fontWeight: 'bold',
              color: '#000000',
              minWidth: '90px',
              textAlign: 'right',
              fontSize: '11px'
            }}>
              Impuesto Interno:
            </label>
            <InputNumber
              size="small"
              style={{ width: '120px', fontSize: '11px' }}
              className="right-aligned-input"
              disabled
              precision={2}
              prefix="$"
              value={totalImpuestoInterno}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </div>

          {/* Subtotal sinIVA */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            justifyContent: 'flex-end',
            borderTop: '1px solid #d9d9d9',
            paddingTop: '6px'
          }}>
            <label style={{ 
              fontWeight: 'bold',
              color: '#000000',
              minWidth: '90px',
              textAlign: 'right',
              fontSize: '11px'
            }}>
              Subtotal sinIVA:
            </label>
            <InputNumber
              size="small"
              style={{ width: '120px', fontSize: '11px' }}
              className="right-aligned-input"
              disabled
              precision={2}
              prefix="$"
              value={totalSubtotalSinIva}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </div>

          {/* Impuesto IVA */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            justifyContent: 'flex-end',
            borderTop: '1px solid #d9d9d9',
            paddingTop: '6px'
          }}>
            <label style={{ 
              fontWeight: 'bold',
              color: '#000000',
              minWidth: '90px',
              textAlign: 'right',
              fontSize: '11px'
            }}>
               IVA:
            </label>
            <InputNumber
              size="small"
              style={{ width: '100px', fontSize: '11px' }}
              className="right-aligned-input"
              disabled
              precision={2}
              prefix="$"
              value={totalIva}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </div>

          {/* Columna de SubTotal General, Percepciones y Total General */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',  // Alinea los elementos verticalmente
            gap: '6px'
          }}>
            {/* SubTotal General */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              justifyContent: 'flex-end',
              borderTop: '1px solid #d9d9d9',
              paddingTop: '6px'
            }}>
              <label style={{ 
                fontWeight: 'bold',
                color: '#000000',
                minWidth: '90px',
                textAlign: 'right',
                fontSize: '11px'
              }}>
                SubTotal General:
              </label>
              <InputNumber
                size="small"
                style={{ width: '120px', fontSize: '11px' }}
                className="right-aligned-input"
                disabled
                precision={2}
                prefix="$"
                value={totalGeneral}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </div>

            {/* Percepciones */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              justifyContent: 'flex-end'
            }}>
              <label style={{ 
                fontWeight: 'bold',
                color: '#000000',
                minWidth: '90px',
                textAlign: 'right',
                fontSize: '11px'
              }}>
                Percepciones:
              </label>
              <InputNumber
                size="small"
                style={{ width: '120px', fontSize: '11px' }}
                className="right-aligned-input"
                disabled
                precision={2}
                prefix="$"
                value={totalPercepciones}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </div>

            {/* Total General */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              justifyContent: 'flex-end',
              borderTop: '1px solid #d9d9d9',
              paddingTop: '6px'
            }}>
              <label style={{ 
                fontWeight: 'bold',
                color: '#000000',
                minWidth: '90px',
                textAlign: 'right',
                fontSize: '11px'
              }}>
                Total General:
              </label>
              <InputNumber
                size="small"
                style={{ width: '120px', fontSize: '11px' }}
                className="right-aligned-input"
                disabled
                precision={2}
                prefix="$"
                value={totalGeneral + totalPercepciones}  // Suma del total general más percepciones
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sección inferior con Percepciones y Totales */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: '#f5f5f5',
        padding: '8px 12px',
        borderRadius: '4px',
        marginTop: '8px',
        gap: '12px'
      }}>
        {/* Lado izquierdo: Selección de percepciones */}
        <div style={{ width: '60%', minWidth: '400px' }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '8px'
          }}>
            {/* Sección de Percepciones */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '8px'
            }}>
              {/* Select e Input de percepciones */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '6px',
                flexWrap: 'wrap'
              }}>
                <label style={{ 
                  fontWeight: 'bold',
                  color: '#000000',
                  width: '80px',
                  fontSize: '11px'
                }}>
                  Percepciones:
                </label>
                <Select
                  size="small"
                  style={{ width: '220px', fontSize: '11px' }}
                  placeholder="Seleccionar Percepciones"
                  loading={loadingPercepciones}
                  value={selectedPercepcion}
                  onChange={(value) => setSelectedPercepcion(value)}
                  showSearch
                  optionFilterProp="children"
                  disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
                >
                  {percepciones.map(perc => (
                    <Option key={perc.id} value={perc.id}>
                      {`${perc.provincia} ${perc.sigla}`}
                    </Option>
                  ))}
                </Select>
                <InputNumber
                  size="small"
                  style={{ width: '120px', fontSize: '11px' }}
                  placeholder="Ingrese el valor"
                  precision={2}
                  prefix="$"
                  value={valorPercepcion}
                  onChange={(value) => setValorPercepcion(Number(value) || 0)}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
                />
                <Button 
                  type="primary"
                  size="small"
                  style={{ height: '28px', fontSize: '12px' }}
                  onClick={handleAgregarPercepcion}
                  disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
                >
                  Agregar
                </Button>
              </div>

              {/* Grid de percepciones */}
              <div style={{ 
                backgroundColor: '#f5f5f5',
                padding: '6px',
                borderRadius: '4px'
              }}>
                <Table
                  columns={columnasPercepciones}
                  dataSource={Array.isArray(percepcionesGrid) ? percepcionesGrid : []}
                  pagination={false}
                  size="small"
                  scroll={{ y: 80 }}
                  style={{ backgroundColor: '#f5f5f5', fontSize: '11px' }}
                  rowKey={record => `perc_${record.id}`}
                />
              </div>

              {/* Botones de acción */}
              <div style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',  // Alinea los botones a la derecha
                marginTop: '8px'  // Espacio adicional después del grid
              }}>
                <Button 
                  type="primary"
                  size="small"
                  style={{ height: '28px', fontSize: '12px' }}
                  onClick={handleFinalizarTarea}
                  disabled={isLoadingPadron || isLoading || datosNuevo.length === 0 || isSaving}
                  loading={isSaving}
                >
                  {isSaving ? 'Grabando...' : 'Finalizar Tarea'}
                </Button>
                <Button 
                  size="small"
                  style={{ height: '28px', fontSize: '12px' }}
                  onClick={handleCancelar}
                >
                  Cancelar
                </Button>
                <Button 
                  size="small"
                  style={{ height: '28px', fontSize: '12px' }}
                  onClick={handleListado}
                >
                  Listado
                </Button>
              </div>
            </div>
          </div>
        </div>

      </div>

      <Modal
        title={<span style={{ fontSize: '14px' }}>Confirmar Ingreso</span>}
        open={isModalVisible}
        onOk={handleModalConfirm}
        onCancel={() => setIsModalVisible(false)}
        width={500}
        okButtonProps={{ size: 'small', style: { fontSize: '12px', height: '28px' } }}
        cancelButtonProps={{ size: 'small', style: { fontSize: '12px', height: '28px' } }}
      >
        <Form layout="vertical">
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label={<span style={{ fontSize: '11px' }}>Cantidad</span>}>
                <InputNumber
                  size="small"
                  value={cantidadTemp}
                  onChange={value => setCantidadTemp(Number(value))}
                  style={{ width: '100%', fontSize: '11px' }}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={<span style={{ fontSize: '11px' }}>Precio Unitario</span>}>
                <InputNumber
                  size="small"
                  value={precioUnitarioTemp}
                  onChange={value => setPrecioUnitarioTemp(Number(value))}
                  style={{ width: '100%', fontSize: '11px' }}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={<span style={{ fontSize: '11px' }}>Impuesto Interno</span>}>
                <InputNumber
                  size="small"
                  value={impuestoInternoTemp}
                  onChange={value => setImpuestoInternoTemp(Number(value))}
                  style={{ width: '100%', fontSize: '11px' }}
                  precision={2}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <div style={{ marginTop: '8px', fontSize: '11px' }}>
                <strong>Detalle del Insumo: </strong>
                {ordenSeleccionada?.insumo_descripcion}
              </div>
              <div style={{ marginTop: '4px', fontSize: '11px' }}>
                <strong>Orden de Compra: </strong>
                {ordenSeleccionada?.orden}
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CrearIngresos;
